import {
  PolicyList,
  PolicyPage,
  PolicySection,
  PolicySubsection,
} from '../../components/PolicyPage';

// Posted pending lawyer review (Jim, 2026-10-05). Source draft and the
// decisions behind each clause: business/ops/legal/subscriber-agreement.md.
// Nothing here may describe an unbuilt feature. When an in-app billing page
// ships, add "or from your billing page" to 12.1 in the same change.
const EFFECTIVE_DATE = 'October 6, 2026';
const LAST_UPDATED = 'October 6, 2026';

export function SubscriberAgreement() {
  return (
    <PolicyPage
      seoTitle="Subscriber Agreement — AuctiSync"
      seoDescription="The agreement between Welcome Home Apps LLC and the auction businesses that subscribe to AuctiSync."
      canonicalPath="/subscriber-agreement"
      title="Subscriber Agreement"
      effectiveDate={EFFECTIVE_DATE}
      lastUpdated={LAST_UPDATED}
    >
      <p>
        This Subscriber Agreement (the &ldquo;<strong>Agreement</strong>&rdquo;) is between{' '}
        <strong>Welcome Home Apps LLC</strong>, a Missouri limited liability company that operates
        AuctiSync (&ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>,&rdquo; &ldquo;
        <strong>AuctiSync</strong>&rdquo;), and the auction business that subscribes to AuctiSync
        (&ldquo;<strong>you</strong>,&rdquo; the &ldquo;<strong>Subscriber</strong>
        &rdquo;). It applies in addition to our <a href="/terms">Terms of Service</a> and{' '}
        <a href="/privacy">Privacy Policy</a>. If this Agreement and the Terms of Service conflict
        on anything about your subscription, this Agreement controls. If you have a signed
        Enterprise agreement with us, that agreement controls over both.
      </p>
      <p>
        By clicking &ldquo;I agree,&rdquo; or by using AuctiSync as a subscriber, you accept this
        Agreement on behalf of your business, and you confirm you are authorized to do so.
      </p>

      <PolicySubsection title="The short version (not a substitute for the full text)">
        <PolicyList
          items={[
            <span>
              You pay a monthly subscription for the plan you choose. You can cancel any time, and
              you keep full use of your plan until the end of the month you&apos;ve paid for.
            </span>,
            <span>
              Your bidders pay <strong>you</strong>, directly, through your own Stripe account.
              AuctiSync takes no cut of your sales.
            </span>,
            <span>
              Your data is yours. We use it to run your auctions, we don&apos;t sell it, and you can
              take a copy with you.
            </span>,
            <span>
              You run your auctions: your items, your terms of sale, your licenses, your compliance.
            </span>,
            <span>
              If a payment fails, your sales already running keep running while you fix it.
            </span>,
          ]}
        />
      </PolicySubsection>

      <PolicySection title="1. Your account">
        <p>
          1.1 <strong>Who can subscribe.</strong> AuctiSync is for businesses that run auctions. You
          must give us accurate business and contact information and keep it current.
        </p>
        <p>
          1.2 <strong>Your owner account.</strong> The person who creates your account is its owner.
          The owner controls who on your staff gets access and what they can do. You are responsible
          for everything done under your account, including by your staff, and for keeping sign-in
          details secure. Tell us at once at{' '}
          <a href="mailto:support@auctisync.com">support@auctisync.com</a> if you believe your
          account has been used without permission.
        </p>
      </PolicySection>

      <PolicySection title="2. What AuctiSync provides">
        <p>
          2.1 <strong>The service.</strong> AuctiSync gives you software to run online auctions:
          cataloging your items, running your sales and bidding, invoicing your buyers, taking their
          payments through Stripe, managing your bidders, and the features included in your plan as
          described at <a href="/pricing">auctisync.com/pricing</a> when you subscribe or change
          plans (the &ldquo;<strong>Service</strong>&rdquo;).
        </p>
        <p>
          2.2 <strong>Your site.</strong> Your auctions are published on AuctiSync. On the Growth
          plan and above, your site can carry your own branding and run on a domain you own. You
          keep ownership of your domain and control of its DNS; we connect it to the Service when
          you point it to us.
        </p>
        <p>
          2.3 <strong>Getting set up.</strong> We help you through onboarding: your branding (Growth
          and above), connecting your payouts, importing your bidders, building your first auction
          and reviewing your site before it goes live.
        </p>
        <p>
          2.4 <strong>Changes to the Service.</strong> We improve AuctiSync continually and may
          change or retire features. We will not take away a core feature of your plan during a
          period you have already paid for without giving you notice and a pro-rated credit.
        </p>
      </PolicySection>

      <PolicySection title="3. Payments from your bidders">
        <p>
          3.1 <strong>You get paid directly.</strong> Bidder payments are processed by Stripe, Inc.
          through Stripe Connect and settle into <strong>your own</strong> Stripe account. You must
          complete Stripe&apos;s onboarding, including identity verification, and agree to
          Stripe&apos;s terms for connected accounts. We never hold your buyers&apos; money.
        </p>
        <p>
          3.2 <strong>Fees on your sales.</strong> AuctiSync charges no fee on your sales.
          Stripe&apos;s processing fees apply under your agreement with Stripe. Buyer&apos;s
          premiums, sales tax and any other charges on your invoices are set by you and belong to
          you.
        </p>
        <p>
          3.3 <strong>Refunds and disputes with buyers</strong> are between you and your buyer. You
          handle them through the tools in AuctiSync and in your Stripe account.
        </p>
      </PolicySection>

      <PolicySection title="4. Your subscription and fees">
        <p>
          4.1 <strong>Plans and prices.</strong> You choose a plan when you subscribe. Prices are
          those shown at <a href="/pricing">auctisync.com/pricing</a> when you subscribe, or in your
          signed Enterprise agreement.
        </p>
        <p>
          4.2 <strong>Billing.</strong> Your subscription is billed monthly, in advance, to the
          payment method you give us, and renews automatically each month until cancelled. Billing
          is handled by Stripe on our behalf.
        </p>
        <p>
          4.3 <strong>Taxes.</strong> Fees do not include taxes. You pay any sales, use or similar
          tax that applies to your subscription.
        </p>
        <p>
          4.4 <strong>Price changes.</strong> We may change subscription prices with at least 30
          days&apos; written notice. The new price applies from your next billing date after the
          notice period.
        </p>
        <p>
          4.5 <strong>No refunds.</strong> Fees are non-refundable, including for partial months,
          except where the law requires a refund or this Agreement says otherwise.
        </p>
        <p>
          4.6 <strong>Trials.</strong> If we offer you a trial, its length and terms are shown when
          you sign up. When it ends, your chosen plan starts and is billed unless you cancel before
          the trial ends.
        </p>
        <p>
          4.7 <strong>Complimentary plans.</strong> We may provide a plan at no charge at our
          discretion. We may end a complimentary plan with 30 days&apos; notice.
        </p>
      </PolicySection>

      <PolicySection title="5. If a payment fails">
        <p>
          5.1 Stripe will retry the charge and email you. You have <strong>7 days</strong> from the
          first failed attempt to update your payment method.
        </p>
        <p>
          5.2 <strong>Your sales keep running.</strong> During those 7 days, and after them,
          auctions that are already live or already scheduled continue to their close, and your
          buyers can still pay you.
        </p>
        <p>
          5.3 After 7 days without payment, you cannot schedule or publish <strong>new</strong>{' '}
          auctions until the balance is paid. If the balance is still unpaid 30 days after that, we
          may end your subscription under section 12.
        </p>
      </PolicySection>

      <PolicySection title="6. Changing plans">
        <p>
          6.1 <strong>Upgrades</strong> take effect immediately. You are charged the pro-rated
          difference for the rest of the current month.
        </p>
        <p>
          6.2 <strong>Downgrades</strong> take effect on your next billing date. From then, features
          and limits of the higher plan stop applying. Your data is not deleted, but anything that
          exceeds the new plan&apos;s limits may become read-only until you remove it or upgrade
          again.
        </p>
      </PolicySection>

      <PolicySection title="7. Your responsibilities as an auctioneer">
        <p>
          7.1 <strong>Your auctions are your business.</strong> You are the seller, or the
          seller&apos;s agent, in every sale you run. AuctiSync is the software you run it on; we
          are not a party to your sales.
        </p>
        <p>
          7.2 <strong>Laws and licenses.</strong> You are responsible for complying with every law
          that applies to your auctions, including auctioneer licensing, sales-tax collection and
          remittance, consumer-protection and advertising law, and the rules for any regulated or
          age-restricted item. AuctiSync&apos;s tools, such as tax reporting, help with this but are
          not legal or tax advice.
        </p>
        <p>
          7.3 <strong>Your items and listings.</strong> You are responsible for what you sell and
          for your listings being accurate, including photos, descriptions, condition, estimates and
          reserves. You may not list anything the <a href="/terms">Terms of Service</a> prohibit.
        </p>
        <p>
          7.4 <strong>AI-assisted drafting.</strong> AuctiSync can draft titles, descriptions and
          categories for your lots. Drafts are suggestions: you review and approve them before they
          are published, and you are responsible for them once published. AuctiSync does not set
          your prices.
        </p>
        <p>
          7.5 <strong>The AuctiSync assistant</strong> can answer questions and, when you confirm,
          make changes in your account for you. You are responsible for the changes you confirm.
        </p>
        <p>
          7.6 <strong>Fair bidding.</strong> You will not bid on your own lots, or let anyone bid on
          your behalf, to raise prices, and you will honor sales made through the Service according
          to your terms of sale.
        </p>
        <p>
          7.7 <strong>Your terms of sale and agreements.</strong> Your terms of sale with your
          buyers, and your agreements with your consignors, are yours. You are responsible for them
          and for following them.
        </p>
        <p>
          7.8 <strong>Your bidders and your messages.</strong> When you import bidders or contact
          them through AuctiSync, you confirm you have the right to do so and to send them the
          messages you send.
        </p>
      </PolicySection>

      <PolicySection title="8. Your data">
        <p>
          8.1 <strong>It&apos;s yours.</strong> You own the information you put into AuctiSync and
          the records of your sales: your catalog, your bidder and consignor records, your invoices
          (&ldquo;<strong>Your Data</strong>&rdquo;).
        </p>
        <p>
          8.2 <strong>How we use it.</strong> We use Your Data only to provide, secure, support and
          improve the Service for you. We do not sell Your Data, and we do not give one house&apos;s
          bidder records to another house. Bidders hold their own AuctiSync accounts, so a bidder
          who registers with more than one house uses one sign-in; each house sees only its own
          relationship with that bidder.
        </p>
        <p>
          8.3 <strong>Aggregated information.</strong> We may combine information from across
          AuctiSync into aggregated statistics that do not identify you, your bidders or any person
          &mdash; for example, typical prices for a category of item &mdash; and use or publish
          those statistics.
        </p>
        <p>
          8.4 <strong>Security.</strong> We protect Your Data with reasonable technical and
          organizational measures, including encryption and daily encrypted backups. If we learn of
          a breach affecting Your Data, we will tell you without undue delay.
        </p>
        <p>
          8.5 <strong>Privacy.</strong> Our <a href="/privacy">Privacy Policy</a> explains how we
          handle personal information. You are responsible for your own privacy notices to your
          bidders and consignors where the law requires them.
        </p>
      </PolicySection>

      <PolicySection title="9. Content and intellectual property">
        <p>
          9.1 <strong>Your content.</strong> You give us a license to host, copy, display and
          process your content &mdash; photos, descriptions, logos, brand colors &mdash; only as
          needed to run the Service for you and to show your auctions to the public.
        </p>
        <p>
          9.2 <strong>Our service.</strong> AuctiSync, its software and its brand belong to us. This
          Agreement gives you the right to use the Service while you subscribe; it does not transfer
          any ownership to you.
        </p>
        <p>
          9.3 <strong>Feedback.</strong> If you send us ideas or suggestions, we may use them
          without any obligation to you.
        </p>
      </PolicySection>

      <PolicySection title="10. Support and availability">
        <p>
          10.1 <strong>Support.</strong> You can reach us at{' '}
          <a href="mailto:support@auctisync.com">support@auctisync.com</a> and through the help
          center and its assistant.
        </p>
        <p>
          10.2 <strong>Availability.</strong> We work to keep AuctiSync available and fast,
          especially while auctions are live, but we do not guarantee uninterrupted service except
          where a signed Enterprise agreement says so. We schedule planned maintenance away from
          live auctions where we reasonably can.
        </p>
      </PolicySection>

      <PolicySection title="11. Confidentiality">
        <p>
          Each of us will keep the other&apos;s non-public business information confidential, use it
          only for the purposes of this Agreement, and protect it with at least reasonable care.
          This does not cover information that is public, already known to the recipient, or that
          the law requires to be disclosed.
        </p>
      </PolicySection>

      <PolicySection title="12. Ending your subscription">
        <p>
          12.1 <strong>You can cancel any time</strong> by emailing{' '}
          <a href="mailto:support@auctisync.com">support@auctisync.com</a>. Cancellation takes
          effect at the end of the month you have already paid for. Until then you keep full use of
          your plan, including scheduling and running new auctions.
        </p>
        <p>
          12.2 <strong>We may end or suspend</strong> your subscription if you materially breach
          this Agreement or the Terms of Service and do not fix it within 14 days of our notice, if
          payment remains unpaid as described in section 5, or immediately where the law requires it
          or where your use puts bidders, other houses or the Service at serious risk.
        </p>
        <p>
          12.3 <strong>Sales in progress.</strong> Auctions that are already live when your
          subscription ends may run to their scheduled close, and your buyers may still pay their
          invoices. You cannot start new auctions after the end date.
        </p>
        <p>
          12.4 <strong>Taking your data with you.</strong> For 30 days after your subscription ends,
          you can ask us for a copy of Your Data in a standard format such as CSV. After that, we
          keep records only as our Privacy Policy describes &mdash; for example, auction and account
          records for seven years, to meet record-keeping, tax and dispute-resolution requirements
          &mdash; and then delete them.
        </p>
        <p>
          12.5 <strong>What continues.</strong> Sections 3.3, 7, 8, 9, 11, 12, 13, 14 and 16
          continue after this Agreement ends.
        </p>
      </PolicySection>

      <PolicySection title="13. Disclaimers">
        <p>
          Except as this Agreement expressly states, the Service is provided &ldquo;as is&rdquo; and
          &ldquo;as available.&rdquo; To the fullest extent the law allows, we disclaim all other
          warranties, express or implied, including merchantability, fitness for a particular
          purpose and non-infringement. We do not guarantee the outcome of any auction, including
          prices, bidder turnout or buyer payment.
        </p>
      </PolicySection>

      <PolicySection title="14. Limitation of liability and indemnity">
        <p>
          14.1 To the maximum extent permitted by law, neither of us is liable to the other for
          indirect, incidental, special, consequential or punitive damages, or for lost profits,
          revenue, data or goodwill.
        </p>
        <p>
          14.2 Our total liability arising out of or relating to this Agreement or the Service will
          not exceed the greater of (a) one hundred dollars ($100) or (b) the fees you paid us in
          the twelve (12) months before the event giving rise to the claim.
        </p>
        <p>
          14.3 You will defend and indemnify us against third-party claims arising from your
          auctions, your items and listings, your content, your dealings with your buyers and
          consignors, or your breach of this Agreement or of the law.
        </p>
      </PolicySection>

      <PolicySection title="15. Publicity">
        <p>
          We will not use your name or logo to promote AuctiSync without your permission. Your
          auctions themselves, and the &ldquo;Powered by AuctiSync&rdquo; mark where your plan shows
          it, are part of the Service and are not promotion under this section.
        </p>
      </PolicySection>

      <PolicySection title="16. General">
        <p>
          16.1 <strong>Governing law and disputes.</strong> This Agreement is governed by the laws
          of the State of Missouri, without regard to its conflict-of-laws principles. Any dispute
          will be brought exclusively in the state or federal courts located in St. Louis County,
          Missouri, and both of us consent to their jurisdiction. Each of us waives any right to a
          jury trial.
        </p>
        <p>
          16.2 <strong>Changes to this Agreement.</strong> We may update this Agreement. We will
          give you at least 30 days&apos; notice of a material change by email or in your account.
          The change applies from the start of your next billing period after the notice period; if
          you do not agree, you may cancel before then.
        </p>
        <p>
          16.3 <strong>Notices.</strong> We send notices to your account&apos;s owner email. You
          send notices to <a href="mailto:support@auctisync.com">support@auctisync.com</a>.
        </p>
        <p>
          16.4 <strong>Assignment.</strong> You may not transfer this Agreement without our consent,
          except to a successor to your auction business that takes on all of its obligations. We
          may transfer it to a successor to our business.
        </p>
        <p>
          16.5 <strong>Events outside our control.</strong> Neither of us is responsible for delays
          or failures caused by events beyond reasonable control, such as outages of internet, cloud
          or payment providers, natural disasters or government action.
        </p>
        <p>
          16.6 <strong>Whole agreement.</strong> This Agreement, the Terms of Service, the Privacy
          Policy and any signed Enterprise agreement are the whole agreement between us about the
          Service. If any part is unenforceable, the rest still applies. Not enforcing a right is
          not giving it up.
        </p>
        <p>
          16.7 <strong>Contact.</strong>{' '}
          <a href="mailto:support@auctisync.com">support@auctisync.com</a>.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}
